"use client";

import type * as React from "react";

import {
  INDIA_NATIONAL_NUMBER_LENGTH,
  toNationalDigits,
} from "@/lib/phone-number";
import { cn } from "@/lib/utils";

type PhoneNumberInputProps = Omit<
  React.ComponentProps<"input">,
  "value" | "defaultValue" | "onChange" | "type" | "children"
> & {
  /** Any format: E.164, "91…", bare national digits, or spaced. */
  value: string | null | undefined;
  /** Emits the national digits only ("9820140079") — never the +91. */
  onChange: (value: string) => void;
  /** Classes for the inner `<input>`; `className` styles the shell. */
  inputClassName?: string;
};

/**
 * Phone field with a fixed India prefix, the same pattern as the sign-in OTP
 * field: the product is India-only, so +91 is a chip, not a picker, and the
 * person types the ten national digits.
 *
 * Value contract: `value` may arrive in any format (a stored E.164 prefill is
 * shown as its national digits); `onChange` emits national digits. Callers
 * normalize to E.164 on the server with `normalizePaymentPhoneNumber`, which
 * reads a bare national number as Indian.
 *
 * `className` lands on the shell (the visible pill), so callers can pass the
 * same height / radius overrides they gave `<Input>`.
 */
export function PhoneNumberInput({
  value,
  onChange,
  className,
  inputClassName,
  placeholder = "98765 43210",
  autoComplete = "tel-national",
  disabled,
  ...props
}: PhoneNumberInputProps) {
  return (
    <div
      data-slot="phone-number-input"
      className={cn(
        "flex h-10 w-full min-w-0 items-center rounded-full border border-black/20 bg-white font-bold text-text-primary shadow-[0_1px_2px_rgba(15,23,42,0.06)] transition-[background-color,border-color,box-shadow] duration-200 md:text-xl dark:border-white/20 dark:bg-white/[0.07] dark:shadow-none",
        "hover:border-black/30 dark:hover:border-white/30 dark:hover:bg-white/[0.09]",
        "focus-within:border-brand/40 focus-within:ring-4 focus-within:ring-brand/20 dark:focus-within:border-brand/50 dark:focus-within:ring-brand/30",
        "has-[input[aria-invalid=true]]:border-destructive has-[input[aria-invalid=true]]:ring-destructive/20 dark:has-[input[aria-invalid=true]]:ring-destructive/40",
        disabled && "pointer-events-none cursor-not-allowed opacity-40",
        className,
      )}
    >
      <span
        aria-hidden="true"
        className="flex h-full shrink-0 items-center gap-1.5 border-r border-black/10 pr-2.5 pl-4 dark:border-white/10"
      >
        <span className="text-[1.05em] leading-none">🇮🇳</span>
        +91
      </span>
      <input
        type="tel"
        inputMode="numeric"
        autoComplete={autoComplete}
        data-slot="input"
        placeholder={placeholder}
        disabled={disabled}
        value={toNationalDigits(value)}
        onChange={(event) =>
          onChange(
            toNationalDigits(event.target.value).slice(
              0,
              INDIA_NATIONAL_NUMBER_LENGTH,
            ),
          )
        }
        className={cn(
          "h-full w-full min-w-0 flex-1 bg-transparent pr-4 pl-2.5 font-[inherit] text-inherit outline-none selection:bg-brand selection:text-text-inverse placeholder:text-text-primary/40 disabled:cursor-not-allowed dark:placeholder:text-white/40",
          inputClassName,
        )}
        {...props}
      />
    </div>
  );
}
